"use client";

import {motion} from "framer-motion";
import {
  Activity,
  Brush,
  Crown,
  ScanLine,
  ShieldCheck,
  Sparkles,
  type LucideIcon,
} from "lucide-react";
import {useTranslations} from "next-intl";

const serviceIcons: LucideIcon[] = [
  Sparkles,
  ScanLine,
  Crown,
  ShieldCheck,
  Activity,
  Brush,
];

export function ServicesGrid() {
  const t = useTranslations("services");

  const services = [1, 2, 3, 4, 5, 6].map((index) => ({
    title: t(`items.${index}.title`),
    description: t(`items.${index}.description`),
    benefits: t(`items.${index}.benefits`),
    process: t(`items.${index}.process`),
    icon: serviceIcons[index - 1],
  }));

  return (
    <section id="services" className="clinic-section mx-auto max-w-7xl px-4 py-16 sm:px-6 md:py-24 lg:px-8">
      <div className="max-w-3xl">
        <p className="clinic-eyebrow">
          {t("eyebrow")}
        </p>
        <h2 className="clinic-display clinic-heading">
          {t("title")}
        </h2>
      </div>

      <motion.div
        className="mt-10 grid gap-4 md:grid-cols-2 xl:grid-cols-3"
        initial="hidden"
        whileInView="visible"
        viewport={{once: true, margin: "-80px"}}
        variants={{
          hidden: {},
          visible: {
            transition: {
              staggerChildren: 0.08,
            },
          },
        }}
      >
        {services.map((service) => {
          const Icon = service.icon;

          return (
            <motion.article
              key={service.title}
              variants={{
                hidden: {opacity: 0, y: 20},
                visible: {opacity: 1, y: 0},
              }}
              transition={{duration: 0.3, ease: "easeOut"}}
              className="clinic-card p-6"
            >
              <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-[#dcebe6] text-[var(--clinic-brand-text)] ring-1 ring-[#175e52]/10 dark:bg-teal-900/50 dark:text-teal-200 dark:ring-white/10">
                <Icon className="h-5 w-5" aria-hidden />
              </div>
              <h3 className="clinic-display mt-5 text-xl font-semibold text-[var(--clinic-ink)]">{service.title}</h3>
              <p className="mt-2 text-sm leading-6 text-[var(--clinic-muted)]">{service.description}</p>
              <p className="mt-5 border-t border-[#173b35]/8 pt-4 text-xs font-bold uppercase tracking-wide text-[var(--clinic-accent-strong)] dark:border-white/10 dark:text-[#ef9b87]">
                {t("benefitsLabel")}
              </p>
              <p className="mt-1 text-sm leading-6 text-[var(--clinic-muted)]">{service.benefits}</p>
              <p className="mt-4 text-xs font-bold uppercase tracking-wide text-[var(--clinic-accent-strong)] dark:text-[#ef9b87]">
                {t("processLabel")}
              </p>
              <p className="mt-1 text-sm leading-6 text-[var(--clinic-muted)]">{service.process}</p>
            </motion.article>
          );
        })}
      </motion.div>
    </section>
  );
}
